import type { FunctionComponent } from 'react';

import { Button, Text } from '@kanda-libs/ks-design-library';

import ApiKey from 'components/company/CompanyLayout/CompanyDetails/Company/ApiKey';
import Bank from 'components/company/CompanyLayout/CompanyDetails/Company/Bank';
import CompanyInfo from 'components/company/CompanyLayout/CompanyDetails/Company/CompanyInfo';
import Contact from 'components/company/CompanyLayout/CompanyDetails/Company/Contact';
import Director from 'components/company/CompanyLayout/CompanyDetails/Company/Director';
import InsuranceDocument from 'components/company/CompanyLayout/CompanyDetails/Company/InsuranceDocument';
import Onboarding from 'components/company/CompanyLayout/CompanyDetails/Company/Onboarding';
import Partner from 'components/company/CompanyLayout/CompanyDetails/Company/Partner';
import SolarTradeInfo from 'components/company/CompanyLayout/CompanyDetails/Company/SolarTradeInfo';
import TradeInfo from 'components/company/CompanyLayout/CompanyDetails/Company/TradeInfo';
import CompanyDeclineButton from 'components/company/CompanyLayout/CompanyDetails/CompanyDeclineButton';
import CompanyVerifyButton from 'components/company/CompanyLayout/CompanyDetails/CompanyVerifyButton';
import { CLASS_NAMES } from 'components/company/CompanyLayout/CompanyDetails/constants';
import useCompanyDetails from 'components/company/CompanyLayout/CompanyDetails/useCompanyDetails';
import Profile from 'components/shared/Profile';

import CompanyFinanceDetails from './Company/CompanyFinanceDetails';
import CompanyJobStats from './Company/CompanyJobStats';
import CompanyRiskStatus from './Company/CompanyRiskStatus';
import CompanyWorkTypes from './Company/CompanyWorkTypes';
import FinanceCalculatorLink from './Company/FinanceCalculatorLink';
import CompanyUsers from './Company/Users';
import type { FormattedDirector } from './helpers';

const CompanyDetails: FunctionComponent = function ({}) {
  const {
    details,
    bank,
    info,
    contact,
    directors,
    lifecycle,
    ownDeposit,
    changeDepositState,
    isSubmitting,
    company,
    showDeclineModal,
    showVerifyModal,
    isSolar,
    billing,
    fca,
    lenderConfigs,
    businessConfig,
    users,
  } = useCompanyDetails();

  return (
    <>
      <Profile.Section title="Users">
        <CompanyUsers users={users} />
      </Profile.Section>
      <div className="flex flex-row w-full gap-4">
        <div className="flex flex-col w-1/2">
          <Contact {...contact} />
        </div>
        <div className="flex flex-col w-1/2">
          <Onboarding />
        </div>
      </div>
      <Profile.Section title="Company info">
        <div className="flex flex-row">
          <div className="flex flex-col w-1/2 mr-4">
            <CompanyInfo {...details} />
            <Bank {...bank} />
            <Partner />
          </div>
          <div className="flex flex-col w-1/2">
            {isSolar ? <SolarTradeInfo {...info} /> : <TradeInfo {...info} />}
            <CompanyFinanceDetails
              fca={fca}
              lenderConfigs={lenderConfigs}
              businessConfig={businessConfig}
            />
            <InsuranceDocument />
          </div>
        </div>

        <div className="flex flex-row">
          <div className="flex flex-col justify-start w-1/2 mr-4">
            <Text text="Directors Info" className={CLASS_NAMES.title} />
            {directors ? (
              <Director
                directors={directors as FormattedDirector[]}
                users={company?.users}
                isSubmitting={isSubmitting}
              />
            ) : (
              <Text
                text="No directors added"
                className="text-14-22 text-neutral-600"
              />
            )}
            <Text
              text="Billing Info"
              className="mt-4 mb-2 text-14-22-em text-neutral-600"
            />
            <Profile.Property
              className="mb-4"
              label="Billing status"
              value={billing}
            />
            <CompanyJobStats />
            <CompanyWorkTypes />
            <CompanyRiskStatus />
          </div>
          <div className="flex flex-col justify-start w-1/2">
            <Text text="Lifecycle" className={CLASS_NAMES.title} />
            <Text
              text={lifecycle}
              className="mb-4 text-14-22 text-neutral-700"
            />
            <CompanyVerifyButton
              showVerifyModal={showVerifyModal}
              isSubmitting={isSubmitting}
            />
            <CompanyDeclineButton
              showDeclineModal={showDeclineModal}
              isSubmitting={isSubmitting}
            />
            <Text text="Takes own deposit?" className={CLASS_NAMES.title} />
            <Text
              text={ownDeposit ? 'Yes' : 'No'}
              className="mb-4 text-14-22 text-neutral-700"
            />
            <Button.Text
              id="change-deposit-state"
              onClick={changeDepositState}
              label={
                ownDeposit
                  ? 'Disable take own deposit'
                  : 'Enable take own deposit'
              }
              size={40}
              disabled={isSubmitting}
              className="mb-4"
            />
            <Text text="API Key" className={CLASS_NAMES.title} />
            <ApiKey apiKey={company?.oid} />
            <FinanceCalculatorLink company={company} />
          </div>
        </div>
      </Profile.Section>
    </>
  );
};

export default CompanyDetails;
